import type { StackProps } from '@hover/blueprint';
import {
  Badge,
  Body,
  Center,
  HStack,
  Icon,
  Image,
  VStack,
  forwardRef,
  motion,
} from '@hover/blueprint';
import type { ThemeTypings } from '@hover/blueprint/chakra';
import { iconImagesAll } from '@hover/icons';
import React from 'react';

interface MiniSplitCardProps extends StackProps {
  eyebrow?: string;
  headline?: string;
  subCopy?: string;
  images?: string[];
  badgeStatus?: string;
  badgeColorScheme?: ThemeTypings['colorSchemes'];
  leftBackground?: string;
  rightBackground?: string;
}

const MINISPLIT_CARD_HEIGHT = '108px';
const MINISPLIT_CARD_WIDTH = '307px';

const MAX_IMAGES = 3;
const MotionImage = motion(Image);

const MiniSplitCard = forwardRef<MiniSplitCardProps, 'div'>(
  (
    {
      eyebrow,
      headline,
      subCopy,
      images,
      badgeStatus,
      badgeColorScheme,
      leftBackground = 'primary.100',
      rightBackground = '#F7F5F5',
      ...props
    },
    ref,
  ) => (
    <HStack
      backgroundColor={rightBackground}
      borderRadius={400}
      height={MINISPLIT_CARD_HEIGHT}
      overflow="hidden"
      ref={ref}
      scrollSnapAlign="start"
      scrollSnapStop="normal"
      width={MINISPLIT_CARD_WIDTH}
      {...props}
    >
      <Center
        backgroundColor={leftBackground}
        height="100%"
        minWidth={MINISPLIT_CARD_HEIGHT}
        position="relative"
      >
        {badgeStatus && (
          // TODO: Update Badge to use the new bauhaus badge once is available
          <Badge colorScheme={badgeColorScheme ?? 'success'} zIndex={999}>
            {badgeStatus}
          </Badge>
        )}
        {images?.length ? (
          images.splice(0, MAX_IMAGES).map((image, index) => {
            const x = index === 0 ? 0 : -5;
            const y = index === 0 ? 0 : -5;
            const rotate = index === 0 ? 1 : index * -5;
            return (
              <MotionImage
                alt={`image-${index}`}
                animate={{ x, y, rotate }}
                borderRadius={300}
                height={500}
                key={rotate}
                objectFit="contain"
                position="absolute"
                shadow="default"
                src={image}
                transition={{ type: 'spring' }}
                zIndex={image.length - index}
              />
            );
          })
        ) : (
          <Icon icon={iconImagesAll} />
        )}
      </Center>
      <VStack alignItems="start" gap={100} p={400} width="100%">
        <Body fontWeight={700} m="0 !important" size={200}>
          {eyebrow}
        </Body>
        <Body fontWeight={700} lineHeight={300} m="0 !important" size={300}>
          {headline}
        </Body>
        <Body lineHeight={300} m="0 !important" size={300} textColor="#666666">
          {subCopy}
        </Body>
      </VStack>
    </HStack>
  ),
);

export { MiniSplitCard };
